import { useCallback, useMemo } from 'react'
import { Image, Pressable, Text, View } from 'react-native'

import debounce from 'lodash/debounce'

import { DS } from '@/design-system'

import { useRouter } from 'expo-router'

import { observer } from 'mobx-react-lite'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { api } from '@ppwr/convex/_generated/api'
import { useQuery } from 'convex/react'
import { GameModeTag } from '../../../../models/api-store'
import { isMaintenanceModeEnabled, showMaintenanceModeAlert } from '@/screens/play-screen/maintenanceMode'
import playTabConfig from '@/screens/play-screen/playTabConfig'
import Card from '@/components/Mini/Card'
import RowCard from '@/components/Mini/RowCard'
import Section from '@/components/Mini/Section'
import { Badge } from '@/screens/play-screen/Sections/TodayLiveGames/Badge'
import { PracticeGameButton } from '@/screens/play-screen/Sections/YourGames'

import JoinGameImage from '@/screens/play-screen/Sections/PlayPoker/assets/JoinGameTile.png'
import CreateGameImage from '@/screens/play-screen/Sections/PlayPoker/assets/CreateGameTile.png'
import LoungesImage from '@/screens/play-screen/Sections/PlayPoker/assets/LoungesTile.png'
import JoinGameThumb from '@/screens/play-screen/Sections/PlayPoker/assets/joinGameThumb.svg'
import CreateGameThumb from '@/screens/play-screen/Sections/PlayPoker/assets/createGameThumb.svg'
import LoungesThumb from '@/screens/play-screen/Sections/PlayPoker/assets/loungesThumb.png'
import HostTournamentThumb from '@/screens/play-screen/Sections/PlayPoker/assets/HostTournamentThumb.png'

const imageAspectRatioA = 205/159;
const imageAspectRatioB = 939/318;

function PlayPoker() {
  return (
    <Section title="Play Poker">
      <JoinGamePlayPokerRow />
      <CreateGamePlayPokerRow />
      <TournamentPlayPokerRow />
    </Section>
  )
}

function BeSocial() {
  return (
    <Section title="Be Social">
      <LoungesPlayPokerRow />
    </Section>
  )
}

// Tile layout (previous home design, kept for reuse)
function PlayPokerGrid (){

  /*const showPracticeGame = playTabConfig.modules.find(m => m.name === 'practiceGame')?.enabled
  const showCreateGame = playTabConfig.modules.find(m => m.name === 'createGame')?.enabled
  const showJoinGame = playTabConfig.modules.find(m => m.name === 'joinGame')?.enabled
  const showLounges = playTabConfig.modules.find(m => m.name === 'lounges')?.enabled*/

  return (
    <View>

      <DS.Text variant="Heading 3/Medium" style={{paddingBottom: 12}}>
        Play Poker
      </DS.Text>

      <View style={{gap:12}}>

        {/*row 2*/}
        <View style={{flexDirection: "row", gap: 8}}>
          <View style={{flex:1}}>
            <JoinGamePlayPokerCard />
          </View>
          <View style={{flex:1}}>
            <CreateGamePlayPokerCard />
          </View>
        </View> 

        <Divider/>
        
        {/*row 3*/}
        <View>
          <View style={{flex:1}}>
            <LoungesPlayPokerCard />
          </View>
        </View> 

      </View>

    </View>
  )
}

function useJoinGame() {

  const router = useRouter()

  const {
    analytics: { setEvent },
    session: { user },
    game: { setTableName }
  } = useStore()

  const onPress = useCallback(() => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    setTableName('')

    setEvent(events.homescreen.join_game, {
      userId: user?._id.toString()
    })
    router.navigate('/game/join')
  }, [user, setEvent, setTableName, router.navigate])

  return useMemo(() => debounce(onPress, 250), [onPress])
}

export const JoinGamePlayPokerCard = observer(() => {
  const handlePress = useJoinGame()

  return (
    <PlayPokerCard
      label="Join Game"
      image={JoinGameImage}
      imageAspectRatio = {imageAspectRatioA}
      handlePress={handlePress}
    />
  )
})

export const JoinGamePlayPokerRow = observer(() => {
  const handlePress = useJoinGame()

  return (
    <RowCard
      title="Join a Game"
      description="Hop into an open table and start playing in seconds."
      Thumbnail={JoinGameThumb}
      onPress={handlePress}
    />
  )
})

function useCreateGame() {

    const router = useRouter()
    const {
      analytics: { setEvent },
      game: { setGameKind, setTableName, clearWalkthrough },
      session: { user }
    } = useStore()
  
    const onPress = useCallback(() => {
      if (isMaintenanceModeEnabled()) {
        showMaintenanceModeAlert()
        return
      }
      clearWalkthrough()
      setTableName('')
      setGameKind(GameModeTag.default)
      setEvent(events.homescreen.create_game, {
        userId: user?._id.toString()
      })
      router.navigate('/game/new')
    }, [user, setEvent, setGameKind, setTableName, clearWalkthrough, router.navigate])

  return useMemo(() => debounce(onPress, 250), [onPress])
}

export const CreateGamePlayPokerCard = observer(() => {
  const handlePress = useCreateGame()

  return (
    <PlayPokerCard
      label="Practice & Play"
      image={CreateGameImage}
      imageAspectRatio = {imageAspectRatioA}
      handlePress={handlePress}
    />
  )
})

export const CreateGamePlayPokerRow = observer(() => {
  const handlePress = useCreateGame()

  return (
    <RowCard
      title="Practice & Play"
      description="Set the stakes and deal yourself in as host."
      Thumbnail={CreateGameThumb}
      onPress={handlePress}
    />
  )
})

function useCreateTournament() {

  const router = useRouter()

  const {
    analytics: { setEvent },
    session: { user }
  } = useStore()

  const onPress = useCallback(() => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    setEvent(events.homescreen.create_tournament, {
      userId: user?._id.toString()
    })
    router.navigate('/mtt/create')
  }, [user, setEvent, router])

  return useMemo(() => debounce(onPress, 250), [onPress])
}

/**
 * Admin-only entry point for MTT creation while the feature is being built out.
 */
export const TournamentPlayPokerRow = observer(() => {
  const store = useStore()
  const handlePress = useCreateTournament()

  const enabled = playTabConfig.modules.find((m) => m.name === 'createTournament')?.enabled
  const isAdmin = store.session?.isAdmin || store.session?.isSuperAdmin
  if (!(enabled && isAdmin)) return null

  return (
    <RowCard
      title="Host a Tournament"
      description="Multi-table play for 10+ players. Blinds rise, tables merge, one winner."
      // 350x264 full-bleed art (wider than the 93x79 tile). Sized to the tile
      // height and pinned left so the trophy stays whole; the overflow trims
      // the card stack, which already runs off the right edge.
      thumbnailImage={{
        source: HostTournamentThumb,
        style: { width: 105, height: 79 }
      }}
      onPress={handlePress}
    />
  )
})

function useLounges() {

  const router = useRouter()

  const {
    analytics: { setEvent },
    session: { user }
  } = useStore()
  const userId = user?.auth0Id ?? ''

  const lounges = useQuery(
    api.lounges.queries.getLoungesForUser,
    userId ? { userId } : 'skip'
  )

  const onPress = useCallback(() => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }

    setEvent(events.homescreen.lounges, {
      userId: user?._id.toString()
    })
 
    router.push('/lounges' as any)

  }, [user, setEvent, router.push])

  const badgeLabel = lounges?.length ? `${lounges.length} lounge${lounges.length === 1 ? '' : 's'}` : undefined

  const handlePress = useMemo(() => debounce(onPress, 250), [onPress])

  return { badgeLabel, handlePress }
}

export const LoungesPlayPokerCard = observer(() => {
  const { badgeLabel, handlePress } = useLounges()

  return (
    <PlayPokerCard
      badgeLabel={badgeLabel}
      label="Lounges"
      image={LoungesImage}
      imageAspectRatio={imageAspectRatioB}
      description="Create a private room, invite your crew, and play together, the way the game was meant to be played."
      handlePress={handlePress}
    />
  )
})

export const LoungesPlayPokerRow = observer(() => {
  const { badgeLabel, handlePress } = useLounges()

  return (
    <RowCard
      title="Lounges"
      description="Invite your crew, and play together, the way the game was meant to be played."
      // Figma: 100x74 illustration at (-3, 4) on a maroon tile
      thumbnailImage={{
        source: LoungesThumb,
        backgroundColor: '#8C0543',
        style: { position: 'absolute', left: -3, top: 4, width: 100, height: 74 }
      }}
      badge={badgeLabel ? { label: badgeLabel, backgroundColor: DS.palette['Brand/Dark Grey'] } : undefined}
      onPress={handlePress}
    />
  )
})

type PlayPokerCardProps = {
  badgeLabel?: string
  label: string
  image: any
  imageAspectRatio: number
  description?: string
  handlePress: () => void
}

export function PlayPokerCard({ badgeLabel=undefined, label, image, imageAspectRatio, description=undefined, handlePress }:PlayPokerCardProps) {

  const showBadge = badgeLabel !== undefined 
  const showDescription = description !== undefined

  return (
    
    <Card style={{padding: 12, maxWidth: "100%"}}>

      <Pressable onPress={handlePress}>

        {showBadge &&
        <View style={{position: "absolute", alignSelf: "flex-end", marginRight: 16, zIndex: 100, top: -12}}>
          <Badge label={badgeLabel} backgroundColor={DS.palette['Brand/Dark Grey']} />
        </View>
        }
        
        <View style={{gap:12}}>
          <View style={{width: '100%', aspectRatio: imageAspectRatio}}> 
            <Image source={image} style={{width: '100%', height: '100%'}} resizeMode="cover" />
          </View>
          <View style={{gap:4}}>
            <DS.Text variant="Heading 6/Medium" style={{textAlign: "center"}}>
              {label}
            </DS.Text>
            {showDescription &&
            <DS.Text variant="Caption/Regular" color="Brand/Dark Grey" style={{textAlign: "center"}}>
              {description}
            </DS.Text>
            }
          </View>
        </View>
      
      </Pressable>
              
    </Card>
 
  )
}

function Divider(){
  return (
    <View style={{height: 1, backgroundColor: DS.palette['Brand/Light Grey']}} />
  )
}

export { PlayPoker, PlayPokerGrid, BeSocial }
